import { WallModel } from '@bw/core';
import type { Material, ProjectDocument } from '@bw/document';
// Deliberately NOT importing the PDF composer here: pdf-lib is ~450 kB and
// belongs in the export worker. The main-thread fallback loads it on demand,
// so a user who never exports never downloads it.
import { paintRaster, rasterLayout, sheetCount } from '@bw/export';
import type { RasterResolution, RasterTarget, SheetSetup } from '@bw/export';
import type { ExportRequest, ExportResponse } from '../workers/export.worker.js';

let worker: Worker | null = null;
let nextId = 1;
/** A 20,000-brick set of sheets composes in a couple of seconds. */
const EXPORT_TIMEOUT_MS = 60_000;

function ensureWorker(): Worker | null {
  if (worker !== null) return worker;
  try {
    worker = new Worker(new URL('../workers/export.worker.ts', import.meta.url), { type: 'module' });
    return worker;
  } catch {
    return null;
  }
}

export interface ExportOutcome {
  readonly bytes: Uint8Array;
  readonly detail: string;
  readonly sheetCount: number;
}

function run(request: ExportRequest): Promise<ExportOutcome> {
  const active = ensureWorker();
  if (active === null) return Promise.reject(new Error('This browser cannot export in the background'));
  return new Promise((resolve, reject) => {
    let settled = false;
    const finish = (run: () => void): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      active.removeEventListener('message', onMessage);
      active.removeEventListener('error', onFailure);
      active.removeEventListener('messageerror', onFailure);
      run();
    };
    const onFailure = (): void =>
      finish(() => reject(new Error('The background exporter stopped responding.')));
    const timer = setTimeout(onFailure, EXPORT_TIMEOUT_MS);
    const onMessage = (event: MessageEvent<ExportResponse>): void => {
      if (event.data.id !== request.id) return;
      const data = event.data;
      finish(() => {
        if (data.ok) resolve({ bytes: data.bytes, detail: data.detail, sheetCount: data.sheetCount });
        else reject(new Error(data.error));
      });
    };
    active.addEventListener('message', onMessage);
    active.addEventListener('error', onFailure);
    active.addEventListener('messageerror', onFailure);
    active.postMessage(request);
  });
}

const materialsOf = (project: ProjectDocument): Material[] => [...project.materials];

export async function exportPdf(project: ProjectDocument, setup: SheetSetup): Promise<ExportOutcome> {
  const model = project.activeWall.model;
  const request: ExportRequest = {
    id: nextId++,
    kind: 'pdf',
    records: model.toRecords(),
    perpendGapMm: model.perpendGapMm,
    materials: materialsOf(project),
    setup,
  };
  try {
    return await run(request);
  } catch (error) {
    // No worker, or it failed to start: the identical code path runs inline.
    if (!(error instanceof Error) || !error.message.includes('background')) throw error;
    const bounds = model.bounds();
    if (bounds === null) throw new Error('Lay at least one brick before exporting');
    const [{ planTiles }, { renderSheetsToPdf }] = await Promise.all([
      import('@bw/export'),
      import('@bw/export/pdf'),
    ]);
    const plan = planTiles(bounds, setup);
    const result = await renderSheetsToPdf({ model, materials: materialsOf(project), setup, plan });
    return {
      bytes: result.bytes,
      sheetCount: result.sheetCount,
      detail: `${result.sheetCount} sheet${result.sheetCount === 1 ? '' : 's'} · ${setup.sheet} ${setup.orientation} · 1:${setup.ratio}`,
    };
  }
}

export async function exportPng(
  project: ProjectDocument,
  resolution: RasterResolution,
): Promise<ExportOutcome> {
  const model = project.activeWall.model;
  const request: ExportRequest = {
    id: nextId++,
    kind: 'png',
    records: model.toRecords(),
    perpendGapMm: model.perpendGapMm,
    materials: materialsOf(project),
    resolution,
  };
  try {
    return await run(request);
  } catch (error) {
    if (!(error instanceof Error) || !error.message.includes('background')) throw error;
    return exportPngOnMainThread(model, project, resolution);
  }
}

async function exportPngOnMainThread(
  model: WallModel,
  project: ProjectDocument,
  resolution: RasterResolution,
): Promise<ExportOutcome> {
  const bounds = model.bounds();
  if (bounds === null) throw new Error('Lay at least one brick before exporting a PNG');
  const layout = rasterLayout(bounds, resolution);
  const canvas = document.createElement('canvas');
  canvas.width = layout.width;
  canvas.height = layout.height;
  const context = canvas.getContext('2d');
  if (context === null) throw new Error('Canvas 2D is unavailable');
  paintRaster(context as unknown as RasterTarget, model, (brick) => project.colourFor(brick), layout);
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (blob === null) throw new Error('Could not encode the PNG');
  return {
    bytes: new Uint8Array(await blob.arrayBuffer()),
    sheetCount: 1,
    detail: `${layout.width} × ${layout.height} px · all ${model.size} bricks fitted`,
  };
}

/** Live sheet count for the panel, recomputed as the setup changes. */
export function previewSheetCount(project: ProjectDocument, setup: SheetSetup): number | null {
  const bounds = project.activeWall.model.bounds();
  if (bounds === null) return null;
  try {
    return sheetCount(bounds, setup);
  } catch {
    return null;
  }
}

export function downloadBytes(name: string, bytes: Uint8Array, type: string): void {
  // Copy into a plain ArrayBuffer: a transferred buffer may be typed as shared.
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  const blob = new Blob([copy.buffer as ArrayBuffer], { type });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = name;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
